import { Code, ConnectError } from '@connectrpc/connect'
import { notifyComposer } from '../stores/composer-notice-store'
import { useAidaChatCommands } from '../stores/chat-commands'

export type AidaChatErrorKind =
  | 'conversation_busy'
  | 'action_required'
  | 'session_limit_reached'
  | 'daily_message_limit_reached'
  | 'subscription_required'
  | 'message_too_long'
  | 'empty_message'
  | 'unknown'

export const PENDING_ACTION_UPLOAD_TOAST = {
  title: "Can't upload while an action is pending",
  description: 'Finish the coin purchase for the waiting generation, then try again.',
} as const

export type AidaRpcErrorSurface = 'session_full' | 'pending_action' | 'silent' | 'fallback'

export function toAidaChatError(error: unknown): AidaChatErrorKind {
  if (!(error instanceof ConnectError)) {
    return 'unknown'
  }
  const message = error.rawMessage.toLowerCase()
  switch (error.code) {
    case Code.FailedPrecondition: {
      if (message === 'daily_message_limit_reached') {
        return 'daily_message_limit_reached'
      }
      if (message === 'subscription_required') {
        return 'subscription_required'
      }
      if (isActionRequiredMessage(message)) {
        return 'action_required'
      }
      if (message.includes('busy')) {
        return 'conversation_busy'
      }
      if (message === 'session_limit_reached' || message.includes('session')) {
        return 'session_limit_reached'
      }
      return 'unknown'
    }
    case Code.InvalidArgument: {
      if (message.includes('long')) {
        return 'message_too_long'
      }
      if (isActionRequiredMessage(message)) {
        return 'action_required'
      }
      if (message.includes('required') || message.includes('empty')) {
        return 'empty_message'
      }
      return 'unknown'
    }
    default: {
      return 'unknown'
    }
  }
}

export function aidaRpcErrorSurface(kind: AidaChatErrorKind): AidaRpcErrorSurface {
  switch (kind) {
    case 'session_limit_reached': {
      return 'session_full'
    }
    case 'action_required': {
      return 'pending_action'
    }
    case 'conversation_busy':
    case 'message_too_long':
    case 'empty_message': {
      return 'silent'
    }
    default: {
      return 'fallback'
    }
  }
}

export function restoresDraftOnSendFailure(kind: AidaChatErrorKind) {
  switch (kind) {
    case 'conversation_busy':
    case 'action_required':
    case 'session_limit_reached':
    case 'daily_message_limit_reached':
    case 'subscription_required':
    case 'message_too_long':
    case 'empty_message': {
      return true
    }
    default: {
      return false
    }
  }
}

export function notifyAidaRpcSurface(surface: AidaRpcErrorSurface) {
  switch (surface) {
    case 'session_full': {
      notifyComposer({
        id: 'chat-full',
        tone: 'error',
        title: 'This chat is full. Start a new one.',
        action: {
          label: 'New chat',
          onClick: () => {
            useAidaChatCommands.getState().onNewChat()
          },
        },
      })
      return
    }
    case 'pending_action': {
      notifyComposer({
        id: 'pending-action',
        tone: 'warning',
        title: PENDING_ACTION_UPLOAD_TOAST.title,
        description: PENDING_ACTION_UPLOAD_TOAST.description,
      })
      return
    }
    case 'silent': {
      return
    }
    case 'fallback': {
      return
    }
  }
}

function isActionRequiredMessage(message: string) {
  return (
    message.includes('action_required') ||
    message.includes('action required') ||
    message.includes('pending action')
  )
}
